import * as React from "react";
import { motion } from "framer-motion";
import { styled } from "../../theme";

import { useProductState } from "context/AppContext";
import ProductGroupItem from "./ProductGroupItem";
import { Product, Option } from "db";

import Control from "./Control";
import Preview from "./Preview";
import InfoPopup from "./InfoPopup";

export interface Props {
  pageHeight: number;
  onClose: () => void;
}

export interface IConfiguratorContext {
  showOptionInfo: (option: Option) => void;
}

export const ConfiguratorContext = React.createContext<IConfiguratorContext | undefined>(undefined);

export default function Configurator({ pageHeight, onClose }: Props) {
  const product = useProductState();
  const [optionInfo, setOptionInfo] = React.useState<Option | null>(null);

  const showOptionInfo = (option: Option) => {
    setOptionInfo(option);
  };

  let price = calcPrice(product);
  let all = allActive(product);
  return (
    <ConfiguratorContext.Provider value={{ showOptionInfo }}>
      <ConfiguratorContainer style={{ height: `${pageHeight}px` }}>
        <Preview onClose={() => onClose()} codes={all.map((a) => a.code)} />
        <Input
          key="input"
          initial={{
            translateX: "-50%"
          }}
          animate={{
            translateX: 0
          }}
          exit={{
            translateX: "-100%"
          }}
        >
          <Content>
            <GroupList
              animate={{
                transition: { staggerChildren: 0.07, delayChildren: 0.2 }
              }}
              exit={{
                transition: { staggerChildren: 0.05, staggerDirection: -1 }
              }}
            >
              {product.groups.map((group, n) => (
                <ProductGroupItem key={n} index={n} group={group} />
              ))}
            </GroupList>
          </Content>
        </Input>
        <Control price={price} />
        <InfoPopup
          option={optionInfo}
          onClose={() => {
            setOptionInfo(null);
          }}
        />
      </ConfiguratorContainer>
    </ConfiguratorContext.Provider>
  );
}

export function useConfigurator() {
  const context = React.useContext(ConfiguratorContext);
  if (context === undefined) {
    throw new Error("useConfigurator must be used within a ConfiguratorContextProvider");
  }
  return context;
}

export function calcPrice(product: Product): number {
  let result = product.basePrice;
  product.groups.forEach((g) => {
    g.accessories.forEach((a) => {
      a.options.forEach((o) => {
        if (o.selected && o.price !== undefined) {
          result += o.price;
        }
      });
    });
  });
  return result;
}

type Temp = {
  code: string;
  title: string;
};
function allActive(product: Product): Temp[] {
  let result: Temp[] = [];
  product.groups.forEach((g) => {
    g.accessories.forEach((a) => {
      a.options.forEach((o) => {
        if (o.selected && o.price !== undefined) {
          result.push({
            code: o.code,
            title: o.name
          });
        }
      });
    });
  });
  return result;
}

const _ConfiguratorContainer = styled.div`
  position: fixed;
  z-index: 5;
  right: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
  overflow: hidden;
  display: flex;
  align-items: stretch;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const ConfiguratorContainer = motion.custom(_ConfiguratorContainer);

const Content = styled.div`
  flex: 1 1 auto;
  overflow-y: scroll;
`;

const _GroupList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;
const GroupList = motion.custom(_GroupList);

const _Input = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 40%;
  height: 100%;
  background-color: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    top: 30%;
    height: 60%;
    width: 100%;
  }
`;

const Input = motion.custom(_Input);
